import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../SupabaseClient.tsx";
import "./Savings.css";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import ActionButton from "../components/ActionButton.tsx";
import { PiPlus } from "react-icons/pi";

interface OrderData {
  id: string;
  purchase_date: string;
  reimbursable_amount: number;
}

interface ChartDataPoint {
  date: string;
  total: number;
  formattedDate: string;
}

interface SavingsChartData {
  purchase_date: string;
  amount: number;
}

const Savings = () => {
  const [orders, setOrders] = useState<OrderData[]>([]);
  const [chartData, setChartData] = useState<ChartDataPoint[]>([]);
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);

      // Fetch orders for total amount display
      const { data: ordersData, error: ordersError } = await supabase
        .from("view_reimburse_amount")
        .select("id, purchase_date, reimbursable_amount")
        .order("purchase_date", { ascending: true });

      if (ordersError) {
        console.error("Error fetching orders:", ordersError);
      } else {
        setOrders(ordersData || []);
      }

      // Fetch chart data from the view
      const { data: chartViewData, error: chartError } = await supabase
        .from("view_savings_chart_data")
        .select("purchase_date, amount");

      if (chartError) {
        console.error("Error fetching chart data:", chartError);
      } else {
        prepareChartData(chartViewData || []);
      }

      setLoading(false);
    };
    fetchOrders();
  }, []);

  // Function to prepare running total data for the chart
  const prepareChartData = (savingsData: SavingsChartData[]) => {
    if (!savingsData.length) return;

    // Data is already in ascending order from the view
    let runningTotal = 0;
    const data: ChartDataPoint[] = [];

    // Add starting point (one day before first entry with 0 amount)
    const earliestDate = new Date(savingsData[0].purchase_date);
    const dayBefore = new Date(earliestDate);
    dayBefore.setDate(dayBefore.getDate() - 1);

    data.push({
      date: dayBefore.toISOString().split("T")[0],
      total: 0,
      formattedDate: formatDate(dayBefore),
    });

    // Calculate running total for each date
    savingsData.forEach((item) => {
      runningTotal += item.amount;
      data.push({
        date: item.purchase_date,
        total: runningTotal,
        formattedDate: formatDate(new Date(item.purchase_date)),
      });
    });

    // Add today if it's not already in the data
    const today = new Date();
    const todayString = today.toISOString().split("T")[0];
    if (data[data.length - 1].date !== todayString) {
      data.push({
        date: todayString,
        total: runningTotal,
        formattedDate: "Today",
      });
    }

    setChartData(data);
    setTotalAmount(runningTotal);
  };

  // Helper function to format dates for display
  const formatDate = (date: Date): string => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const dateObj = new Date(date);
    dateObj.setHours(0, 0, 0, 0);

    if (dateObj.getTime() === today.getTime()) {
      return "Today";
    }

    return dateObj.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // Custom tooltip component
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="savings-tooltip">
          <p className="savings-tooltip-date">
            {payload[0].payload.formattedDate}
          </p>
          <p className="savings-tooltip-value">
            ${payload[0].value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
          </p>
          <p className="savings-tooltip-label">Total Emergency Fund</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="global-page-screen">
      <div className="page">
        <div className="page-header">
          <div className="page-header-title">
            <h1>Savings</h1>
            <p>
              By delaying reimbursement, you can treat your eligible receipts as
              an emergency fund when unexpected circumstances arise.
            </p>
          </div>
        </div>
        <div className="savings-container">
          <div className="savings-summary">
            <div className="savings-total">
              <h2 className="container-label-header">Your Emergency Fund</h2>
              <h1>
                ${totalAmount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
              </h1>
              <p>Available for tax-free withdrawal anytime</p>
            </div>
            <div className="savings-info">
              <p>
                This represents the total amount of eligible expenses you've
                logged that can be reimbursed immediately if needed.
              </p>
            </div>
          </div>
          <div className="savings-graph">
            {loading ? (
              <div className="savings-loading">Loading chart data...</div>
            ) : chartData.length > 0 ? (
              <div className="savings-chart-container">
                <h2 className="container-label-header">Your Emergency Fund Growth Over Time</h2>
                <ResponsiveContainer width="100%" height={400}>
                  <AreaChart
                    data={chartData}
                    margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="var(--color-grey-200)"
                    />
                    <XAxis
                      dataKey="formattedDate"
                      tick={{ fill: "var(--color-grey-600)", fontSize: 12 }}
                      tickMargin={10}
                      axisLine={{ stroke: "var(--color-grey-300)" }}
                    />
                    <YAxis
                      tickFormatter={(value) =>
                        `$${value
                          .toFixed(0)
                          .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`
                      }
                      tick={{ fill: "var(--color-grey-600)", fontSize: 12 }}
                      axisLine={{ stroke: "var(--color-grey-300)" }}
                      tickMargin={10}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <ReferenceLine y={0} stroke="var(--color-grey-400)" />
                    <Area
                      type="monotone"
                      dataKey="total"
                      stroke="var(--color-blue-500)"
                      fill="var(--color-blue-50)"
                      strokeWidth={2}
                      activeDot={{ r: 8, fill: "var(--color-blue-700)" }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="container-empty">
                <p>
                  You haven't recorded any eligible expenses yet. Start adding
                  receipts to build your emergency fund.
                </p>
                <ActionButton
                  onClick={() => navigate("/receipt-upload")}
                  text="Add Receipt"
                  icon={<PiPlus />}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
export default Savings;
